/* eslint-disable react/jsx-props-no-spreading */
/* eslint-disable no-unused-expressions */
/* eslint-disable react/no-children-prop */
/* eslint-disable @typescript-eslint/no-shadow */

import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import Form from 'components/common/Form';
import { Typography, Divider, Box, Button, Modal } from '@mui/material';
import { BaseDrawer } from 'components/styleguide';
import { FormButton } from 'components/common/Form/Fields';
import { useDispatch } from 'react-redux';
import { useApplyCrossMfnTracksMutation, useBulkUpdateTracksApprovedFeesMutation } from 'services/clear/tracks';
import { refetchCueContainers } from 'redux-core/cueContainers/actions';
import { useState } from 'react';
import { showErrorToast, showSuccessToast } from 'components/common/toastNotifications';
import CircularProgress from '@mui/material/CircularProgress';
import { clearancesBundleApi } from 'services/clear/clearances-bundle';
import { fetchFooterInfo } from 'redux-core/overview/actions';
import enhancer from './enhancer';
import TrackList from './TrackList/index';
import { CommonDrawerPropTypes } from '../constants';

const rootT = 'drawers.productionSetup.advancedMfnD';

const DialogExit = ({ open, onClose, onConfirm }) => {
  const [isProcessing, setIsProcessing] = useState(false);
  const { t } = useTranslation();
  const handleConfirm = async () => {
    if (isProcessing) return;
    setIsProcessing(true);
    await onConfirm();
    setIsProcessing(false);
  };

  return (
    <Modal open={open} onClose={onClose}>
      <Box
        sx={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          backgroundColor: 'background.paper',
          padding: 4,
          width: 400,
          textAlign: 'center',
          flexDirection: 'column',
        }}
      >
        <Typography sx={{ mb: 3 }}>{t(`${rootT}.dialogUnsaved`)}</Typography>
        {isProcessing && <CircularProgress />}
        {!isProcessing && (
          <Box sx={{ display: 'flex', justifyContent: 'center', gap: 2 }}>
            <Button color="primary" onClick={onClose}>
              {t(`${rootT}.no`)}
            </Button>
            <Button variant="contained" color="primary" onClick={handleConfirm}>
              {t(`${rootT}.yes`)}
            </Button>
          </Box>
        )}
      </Box>
    </Modal>
  );
};

const Dialog = ({ open, onClose, onConfirm, t }) => {
  const [isProcessing, setIsProcessing] = useState(false);

  const handleConfirm = async () => {
    if (isProcessing) return;
    setIsProcessing(true);
    await onConfirm();
    setIsProcessing(false);
    onClose();
  };

  return (
    <Modal open={open} onClose={onClose}>
      <Box
        sx={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          backgroundColor: 'background.paper',
          padding: 4,
          width: 400,
          textAlign: 'center',
          flexDirection: 'column',
        }}
      >
        <Typography sx={{ mb: 3 }}>{t(`${rootT}.dialog`)}</Typography>
        {isProcessing && <CircularProgress />}
        {!isProcessing && (
          <Box sx={{ display: 'flex', justifyContent: 'center', gap: 2 }}>
            <Button color="primary" onClick={onClose}>
              {t(`${rootT}.no`)}
            </Button>
            <Button variant="contained" color="primary" onClick={handleConfirm}>
              {t(`${rootT}.yes`)}
            </Button>
          </Box>
        )}
      </Box>
    </Modal>
  );
};

const AdvancedMfnDrawer = ({
  onClose,
  payload: { subprojectId },
  advancedMfn,
  setHasUnsavedChanges,
  hasUnsavedChanges,
}) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();

  const [applyCrossMfn] = useApplyCrossMfnTracksMutation();
  const [updateTracksApprovedFees] = useBulkUpdateTracksApprovedFeesMutation();

  const [openDialog, setOpenDialog] = useState(false);

  const onCancel = () => {
    onClose();
  };

  const onUpdate = async () => {
    const linkedTracksIds = advancedMfn.tracks.filter((track) => track.linked).map((track) => track.trackId);
    await updateTracksApprovedFees({
      tracksIds: linkedTracksIds,
    });
    onClose();
    dispatch(fetchFooterInfo());
    return dispatch(refetchCueContainers());
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
  };

  const handleSubmit = async () => {
    setOpenDialog(true);
  };

  const onConfirm = async () => {
    await applyCrossMfn({
      subprojectId,
      ...advancedMfn,
    })
      .unwrap()
      .then(() => {
        showSuccessToast(t(`${rootT}.toastSuccess`));
        setHasUnsavedChanges(false);
      })
      .catch((error) => {
        showErrorToast(t(`${rootT}.toastError`));
        // eslint-disable-next-line no-console
        console.error('Error applying cross MFN:', error);
      });

    dispatch(clearancesBundleApi.util.invalidateTags(['clearanceBundle']));
    dispatch(fetchFooterInfo());

    return dispatch(refetchCueContainers());
  };

  const disableApplyMfnButton = (options) => (
    Array.isArray(options?.tracks) &&
    options.tracks.filter((track) => track.linked).length > 0 &&
    options.tracks.filter((track) => track.linked).length < 2
  ) || hasUnsavedChanges === false;

  const trackIsLinked = advancedMfn?.tracks?.some((track) => track.linked);

  return (
    <Form onSubmit={handleSubmit} testId="bulk-rights-option-drawer">
      <Typography
        sx={{
          fontWeight: 'bold',
          fontSize: '1.5rem',
          display: 'flex',
          justifyContent: 'space-between',
        }}
      >
        {t(`${rootT}.drawerTitle`)}
        <Box sx={{ gap: 2, flexDirection: 'row', display: 'flex' }}>
          <FormButton
            variant="secondary"
            size="xs"
            children="global.forms.applyChanges"
            testId="apply-button"
            alwaysEnabled
            disabled={disableApplyMfnButton(advancedMfn)}
            type="submit"
            style={{
              borderColor: '#a15ce2',
              color: disableApplyMfnButton(advancedMfn) ? '#D3D3D3' : '#a15ce2',
              cursor: disableApplyMfnButton(advancedMfn) ? 'not-allowed' : 'pointer',
            }}
          />
          <FormButton
            alwaysEnabled
            variant="primary"
            size="xs"
            testId="bulk-rights-option-drawer-close"
            children="global.forms.close"
            onClick={() => onCancel()}
          />
        </Box>
      </Typography>

      <Divider sx={{ my: 2 }} />

      <Box sx={{ display: 'flex', flexDirection: 'row', gap: 2 }}>
        <Box>
          <Typography sx={{ fontSize: '13px' }}>
            {t(`${rootT}.instructionalTextPart1`)}{' '}
            <Box component="span" sx={{ fontWeight: 'bold' }}>
              {t(`${rootT}.instructionalTextLink`)}
            </Box>{' '}
            {t(`${rootT}.instructionalTextPart2`)}
          </Typography>

          <Typography sx={{ fontSize: '13px', mt: 1.5 }}>{t(`${rootT}.instructionalText2`)}</Typography>
        </Box>

        <Box sx={{ alignSelf: 'flex-end' }}>
          <FormButton
            variant="secondary"
            size="sm"
            children="drawers.productionSetup.advancedMfnD.updateAprovedFees"
            alwaysEnabled
            style={{
              minWidth: '165px',
              height: '36px',
              padding: '8px 16px',
            }}
            onClick={() => onUpdate()}
            disabled={hasUnsavedChanges || !trackIsLinked}
          />
        </Box>
      </Box>
      <Divider sx={{ my: 2 }} />
      <Box sx={{ mt: 0 }}>
        <TrackList translate={t} subprojectId={subprojectId} setHasUnsavedChanges={setHasUnsavedChanges} />
      </Box>
      <Dialog open={openDialog} onClose={handleCloseDialog} onConfirm={onConfirm} t={t} />
    </Form>
  );
};

AdvancedMfnDrawer.propTypes = {
  onClose: PropTypes.func.isRequired,
  payload: PropTypes.object,
};

const Drawer = ({ open, onClose, ...props }) => {
  const [showExitDialog, setShowExitDialog] = useState(false);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);

  const handleClose = () => {
    if (hasUnsavedChanges) {
      setShowExitDialog(true);
    } else {
      onClose();
    }
  };

  const handleDiscardChanges = () => {
    setShowExitDialog(false);
    onClose();
  };

  return (
    <>
      <BaseDrawer open={open} onClose={handleClose}>
        <AdvancedMfnDrawer
          onClose={handleClose}
          hasUnsavedChanges={hasUnsavedChanges}
          setHasUnsavedChanges={setHasUnsavedChanges}
          {...props}
        />
      </BaseDrawer>
      <DialogExit open={showExitDialog} onClose={() => setShowExitDialog(false)} onConfirm={handleDiscardChanges} />
    </>
  );
};

Drawer.propTypes = {
  ...CommonDrawerPropTypes,
};

export default enhancer(Drawer);
